// Heatmap-overlay för 3D-vyn — beräknar density per grid-cell så
// `ThreeDView` kan färglägga sheet-planes där det är tätt med
// blocks / connectors.
//
// Pure algoritm: inget Three.js-dep. Inputs är block-koord-listor;
// output är en 2D-matris av density-scores 0-1.

export interface DensityPoint {
  x: number;
  y: number;
  /** Valfri vikt (t.ex. connector-end med tyngre vikt). Default 1. */
  weight?: number;
}

export interface DensityGrid {
  /** Grid-cellerna. [row][col] = score 0-1. */
  cells: number[][];
  /** Rader × kolumner. */
  rows: number;
  cols: number;
  /** Ursprungs-bounds (för rendering-koordinater). */
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  /** Max-score innan normalisering — för tooltip "peak = N items". */
  peakCount: number;
}

export interface ComputeOptions {
  /** Antal rader i gridden. Default 20. */
  rows?: number;
  /** Antal kolumner. Default 20. */
  cols?: number;
  /** Om angiven, använd dessa bounds istället för points' AABB. */
  bounds?: { minX: number; minY: number; maxX: number; maxY: number };
}

/**
 * Beräkna density-grid. Varje point binnas i rätt cell; scores
 * normaliseras till 0-1 (peak = 1).
 */
export function computeDensity(
  points: DensityPoint[],
  opts: ComputeOptions = {},
): DensityGrid {
  const rows = opts.rows ?? 20;
  const cols = opts.cols ?? 20;
  const cells: number[][] = Array.from({ length: rows }, () => Array(cols).fill(0));

  if (points.length === 0) {
    return {
      cells, rows, cols,
      bounds: opts.bounds ?? { minX: 0, minY: 0, maxX: 0, maxY: 0 },
      peakCount: 0,
    };
  }

  const bounds = opts.bounds ?? computeBounds(points);
  const w = Math.max(1, bounds.maxX - bounds.minX);
  const h = Math.max(1, bounds.maxY - bounds.minY);

  let peakCount = 0;
  for (const p of points) {
    const col = Math.min(cols - 1, Math.max(0, Math.floor(((p.x - bounds.minX) / w) * cols)));
    const row = Math.min(rows - 1, Math.max(0, Math.floor(((p.y - bounds.minY) / h) * rows)));
    const weight = p.weight ?? 1;
    cells[row][col] += weight;
    if (cells[row][col] > peakCount) peakCount = cells[row][col];
  }

  // Normalisera.
  if (peakCount > 0) {
    for (let r = 0; r < rows; r += 1) {
      for (let c = 0; c < cols; c += 1) {
        cells[r][c] = cells[r][c] / peakCount;
      }
    }
  }

  return { cells, rows, cols, bounds, peakCount };
}

function computeBounds(points: DensityPoint[]) {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  return { minX, minY, maxX, maxY };
}

/**
 * Mappa en score 0-1 till en RGB-färg (blå → grön → gul → röd).
 * Används för tex Three.js mesh vertex-color.
 */
export function scoreToColor(score: number): { r: number; g: number; b: number } {
  const s = Math.max(0, Math.min(1, score));
  if (s < 0.33) {
    // blå → grön
    const t = s / 0.33;
    return { r: 0, g: t, b: 1 - t };
  }
  if (s < 0.66) {
    // grön → gul
    const t = (s - 0.33) / 0.33;
    return { r: t, g: 1, b: 0 };
  }
  // gul → röd
  const raw = (s - 0.66) / 0.34;
  const t = Math.min(1, Math.max(0, raw));
  const g = 1 - t;
  return { r: 1, g: g < 1e-9 ? 0 : g, b: 0 };
}
